/**
 * Tool ROI Users Modal
 * Tools Leaderboard (ROI / Time Saved) row click → per-user contribution
 * for that tool, sorted by time saved (most to least).
 */

import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Users as UsersIcon, RefreshCw, Search, Clock } from 'lucide-react';
import { filterDashboardUsers } from '../../../utils/dashboardUserFilters';
import '../AIGarage.css';

const formatMinutes = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const hrs = Number(minutes) / 60;
  if (hrs >= 10) return `${Math.round(minutes).toLocaleString()}m (${hrs.toFixed(1)} hrs)`;
  return `${Math.round(minutes).toLocaleString()}m`;
};

const getUserInitials = (email) => {
  if (!email) return '?';
  const name = email.split('@')[0];
  const parts = name.split('.');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const AVATAR_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52C4B5'];
const getUserColor = (index) => AVATAR_COLORS[index % AVATAR_COLORS.length];

const ToolRoiUsersModal = ({ isOpen, tool, users, loading, windowLabel, onClose }) => {
  const [search, setSearch] = useState('');

  // Hide excluded internal/product-dev emails (UI only), then apply search
  const filteredUsers = useMemo(() => {
    const visible = filterDashboardUsers(users || []);
    if (!search.trim()) return visible;
    const q = search.toLowerCase();
    return visible.filter(
      (u) =>
        (u.userName || '').toLowerCase().includes(q) ||
        (u.userEmail || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  if (!isOpen || !tool) return null;

  const totalTimeSaved = filteredUsers.reduce((s, u) => s + (u.timeSavedMin || 0), 0);
  const totalSessions = filteredUsers.reduce((s, u) => s + (u.sessions || 0), 0);

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="garage-modal-header">
          <div style={{ flex: 1 }}>
            <h2 className="garage-modal-title" style={{ marginBottom: '4px' }}>
              {tool.name}
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--garage-text-3)', margin: 0 }}>
              Users contributing to ROI · {windowLabel || 'Selected window'}
            </p>
          </div>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="garage-modal-body" style={{ padding: 0 }}>
          {/* Search Bar */}
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'var(--garage-surface)',
            borderBottom: '1px solid var(--garage-border)'
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--garage-text-3)'
              }} />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--garage-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'var(--garage-bg)',
                  color: 'var(--garage-text)'
                }}
              />
            </div>
          </div>

          {/* Section Header */}
          <div style={{
            padding: '12px 24px',
            backgroundColor: 'var(--garage-bg-2)',
            borderBottom: '1px solid var(--garage-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UsersIcon size={18} style={{ color: 'var(--garage-green)' }} />
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--garage-text-2)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Contributors ({filteredUsers.length})
              </span>
            </div>
            {!loading && filteredUsers.length > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--garage-text-3)' }}>
                {totalSessions.toLocaleString()} sessions · {formatMinutes(totalTimeSaved)} saved
              </span>
            )}
          </div>

          {/* Users List */}
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)' }}>
                <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: '12px', fontSize: '14px' }}>Loading contributors...</div>
              </div>
            ) : filteredUsers && filteredUsers.length > 0 ? (
              <div>
                {filteredUsers.map((user, idx) => (
                  <div
                    key={user.userEmail}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '14px 24px',
                      borderBottom: idx < filteredUsers.length - 1 ? '1px solid var(--garage-border-2)' : 'none',
                    }}
                  >
                    {/* Rank */}
                    <div style={{
                      width: '28px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--garage-text-3)',
                      textAlign: 'center'
                    }}>
                      {idx + 1}
                    </div>

                    {/* Avatar */}
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        backgroundColor: getUserColor(idx),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '15px',
                        flexShrink: 0
                      }}
                    >
                      {getUserInitials(user.userEmail)}
                    </div>

                    {/* User Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: 'var(--garage-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.userName || user.userEmail}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--garage-text-3)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: '2px'
                      }}>
                        {user.userEmail}
                      </div>
                    </div>

                    {/* Time saved + sessions */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: 'var(--garage-orange)'
                      }}>
                        <Clock size={14} />
                        {formatMinutes(user.timeSavedMin)}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--garage-text-3)',
                        marginTop: '2px'
                      }}>
                        {user.sessions} session{user.sessions === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : search.trim() ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No users found matching "{search}"
              </div>
            ) : (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No users have contributed to this tool's ROI in this window.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ToolRoiUsersModal;
